/**
 * Класс для управления полем счётчика (увеличение/уменьшение значения)
 */
export class CounterField {
  private container: HTMLElement
  private display: HTMLElement
  private input: HTMLInputElement
  private decreaseButton: HTMLButtonElement
  private increaseButton: HTMLButtonElement
  private value: number
  private name: string
  private minValue: number = 0
  private maxValue: number = Infinity

  private syncBoundsFromDataset(): void {
    this.minValue = parseInt(this.container.dataset.minValue || '0', 10)
    this.maxValue = parseInt(this.container.dataset.maxValue || '1000', 10)
  }

  private syncValueFromInput(): void {
    const inputValue = parseInt(this.input.value, 10)

    if (Number.isFinite(inputValue)) {
      this.value = inputValue
    }
  }

  /**
   * Создаёт экземпляр CounterField
   *
   * @param {HTMLElement} container - Контейнер поля счётчика
   */
  constructor(container: HTMLElement) {
    this.container = container
    this.name = container.dataset.name || 'counter'
    this.display = container.querySelector('[data-counter-display]')!
    this.input = container.querySelector('[data-counter-input]')!
    this.decreaseButton = container.querySelector('[data-counter-decrease]')!
    this.increaseButton = container.querySelector('[data-counter-increase]')!
    this.value = parseInt(this.input.value, 10) || 0
    this.minValue = parseInt(container.dataset.minValue || '0', 10)
    this.maxValue = parseInt(container.dataset.maxValue || '1000', 10)

    this.init()
  }

  /**
   * Инициализирует обработчики событий и начальное состояние
   */
  private init(): void {
    this.decreaseButton.addEventListener('click', () => this.decrease())
    this.increaseButton.addEventListener('click', () => this.increase())
    this.updateUI()

    const form = this.container.closest('form')
    if (form) {
      form.addEventListener('reset', () => {
        setTimeout(() => {
          this.value = parseInt(this.input.value, 10) || 0
          this.updateUI()
        })
      })
    }
  }

  /**
   * Уменьшает значение счётчика на 1
   */
  private decrease(): void {
    this.syncBoundsFromDataset()
    this.syncValueFromInput()

    if (this.value > this.minValue) {
      this.value--
      this.updateUI()
      this.emitChange()
    }
  }

  /**
   * Увеличивает значение счётчика на 1
   */
  private increase(): void {
    this.syncBoundsFromDataset()
    this.syncValueFromInput()

    if (this.value < this.maxValue) {
      this.value++
      this.updateUI()
      this.emitChange()
    }
  }

  /**
   * Обновляет отображение и состояние кнопок
   */
  private updateUI(): void {
    this.syncBoundsFromDataset()

    this.display.textContent = this.value.toString()
    this.input.value = this.value.toString()
    this.decreaseButton.disabled = this.value <= this.minValue
    this.increaseButton.disabled = this.value >= this.maxValue
  }

  /**
   * Генерирует событие изменения значения счётчика
   */
  private emitChange(): void {
    const event = new CustomEvent('counterfield:change', {
      detail: {
        name: this.name,
        value: this.value
      },
      bubbles: true
    })
    this.container.dispatchEvent(event)
  }

  /**
   * Возвращает текущее значение счётчика
   *
   * @return {number} Текущее значение
   */
  public getValue(): number {
    return this.value
  }

  /**
   * Устанавливает значение счётчика
   *
   * @param {number} value - Новое значение (должно быть >= minValue)
   */
  public setValue(value: number): void {
    this.syncBoundsFromDataset()

    if (value >= this.minValue && value <= this.maxValue) {
      this.value = value
      this.updateUI()
    }
  }
}
